import React, { useMemo } from 'react';
import { PIECE_CONFIG, SPECIAL_SHAPES_CONFIG } from '../../lib/shapes';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import { Lock, Sparkles } from 'lucide-react';

// Dynamic confidence values based on current zone
// Zone colors: blue (<40), orange (40-60), green/red (60+)
const getDynamicConfidence = (pieceType, currentConf) => {
  const zone = currentConf < 40 ? 'blue' : currentConf < 60 ? 'orange' : 'greenred';
  
  const dynamicValues = {
    cube:     { blue: 2, orange: 3, greenred: 4 },
    sphere:   { blue: 1, orange: 2, greenred: 3 },
    pyramid:  { blue: 3, orange: 4, greenred: 5 },
    hexagon:  { blue: 1, orange: 2, greenred: 3 },
    diamond:  { blue: 1, orange: 1, greenred: 1 },
    star:     { blue: 4, orange: 5, greenred: 6 },
    void:     { blue: -2, orange: -2, greenred: -2 },
    pen:      { blue: '~', orange: '~', greenred: '~' }, // Variable based on line straightness
  };
  
  // Special shapes always -2
  if (SPECIAL_SHAPES_CONFIG[pieceType]) {
    return -2;
  }
  
  const config = dynamicValues[pieceType];
  if (config) {
    return config[zone];
  }
  
  return 2; // fallback
};

const SHAPE_COLORS = {
  cube: '#18181B', sphere: '#2563EB', pyramid: '#10B981',
  hexagon: '#7C3AED', diamond: '#F59E0B', star: '#EF4444',
  void: '#71717A', chaos: '#EA580C',
  // Special shapes
  cross: '#DC2626', arrow: '#0891B2', crescent: '#7C3AED',
  bolt: '#FBBF24', wave: '#06B6D4', heart: '#EC4899',
  cloud: '#94A3B8', spiral: '#8B5CF6', fragment: '#64748B', eye: '#14B8A6',
};

// Palette shapes are now white with a border - color is chosen separately
function miniShapeSvg(type, isSelected) {
  // White fill with gray outline (or inverted for selected state)
  const fill = isSelected ? '#fff' : '#FFFFFF';
  const stroke = isSelected ? '#A1A1AA' : '#D4D4D8';
  const strokeWidth = isSelected ? 0 : 1.5;
  const h = 8;
  const cx = 14;
  const cy = 14;

  switch (type) {
    case 'cube':
      return `<rect x="${cx - h}" y="${cy - h}" width="${h * 2}" height="${h * 2}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" rx="2"/>`;
    case 'sphere':
      return `<circle cx="${cx}" cy="${cy}" r="${h}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    case 'pyramid':
      return `<polygon points="${cx},${cy - h} ${cx - h},${cy + h} ${cx + h},${cy + h}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    case 'hexagon': {
      const pts = Array.from({ length: 6 }, (_, i) => {
        const a = (Math.PI / 3) * i - Math.PI / 6;
        return `${cx + h * Math.cos(a)},${cy + h * Math.sin(a)}`;
      }).join(' ');
      return `<polygon points="${pts}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    }
    case 'diamond':
      return `<polygon points="${cx},${cy - h} ${cx + h * 0.7},${cy} ${cx},${cy + h} ${cx - h * 0.7},${cy}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    case 'star': {
      const outerR = h;
      const innerR = h * 0.4;
      const pts = Array.from({ length: 10 }, (_, i) => {
        const a = (Math.PI / 5) * i - Math.PI / 2;
        const r = i % 2 === 0 ? outerR : innerR;
        return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
      }).join(' ');
      return `<polygon points="${pts}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    }
    case 'void':
      return `<rect x="${cx - h}" y="${cy - h}" width="${h * 2}" height="${h * 2}" fill="none" stroke="#71717A" stroke-width="1.5" stroke-dasharray="3 3" rx="2"/>`;
    case 'pen':
      // Pen icon - pencil shape
      return `<line x1="${cx-h}" y1="${cy+h}" x2="${cx+h}" y2="${cy-h}" stroke="${stroke}" stroke-width="2" stroke-linecap="round"/>
              <polygon points="${cx-h},${cy+h} ${cx-h+3},${cy+h-5} ${cx-h+5},${cy+h-3}" fill="${stroke}"/>`;
    // Special shapes - also white with border
    case 'cross':
      return `<path d="M${cx-2},${cy-h} h4 v${h-2} h${h-2} v4 h-${h-2} v${h-2} h-4 v-${h-2} h-${h-2} v-4 h${h-2}z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    case 'arrow':
      return `<polygon points="${cx+h},${cy} ${cx-h/2},${cy-h} ${cx-h/2},${cy-h/3} ${cx-h},${cy-h/3} ${cx-h},${cy+h/3} ${cx-h/2},${cy+h/3} ${cx-h/2},${cy+h}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    case 'crescent':
      return `<path d="M${cx+h/2},${cy-h} A${h},${h} 0 1,0 ${cx+h/2},${cy+h} A${h*0.6},${h*0.6} 0 1,1 ${cx+h/2},${cy-h}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    case 'bolt':
      return `<polygon points="${cx+h/2},${cy-h} ${cx-h/3},${cy-h/4} ${cx+h/4},${cy-h/4} ${cx-h/2},${cy+h} ${cx+h/3},${cy+h/4} ${cx-h/4},${cy+h/4}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    case 'wave':
      return `<path d="M${cx-h},${cy} Q${cx-h/2},${cy-h} ${cx},${cy} Q${cx+h/2},${cy+h} ${cx+h},${cy}" stroke="${stroke}" stroke-width="3" fill="none"/>`;
    case 'heart':
      return `<path d="M${cx},${cy+h/2} C${cx-h},${cy-h/2} ${cx-h},${cy-h} ${cx},${cy-h/3} C${cx+h},${cy-h} ${cx+h},${cy-h/2} ${cx},${cy+h/2}z" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    case 'cloud':
      return `<ellipse cx="${cx}" cy="${cy}" rx="${h}" ry="${h*0.6}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/><circle cx="${cx-h/2}" cy="${cy-h/3}" r="${h/2}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/><circle cx="${cx+h/2}" cy="${cy-h/3}" r="${h/2}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    case 'spiral':
      return `<circle cx="${cx}" cy="${cy}" r="${h}" fill="none" stroke="${stroke}" stroke-width="2"/><circle cx="${cx}" cy="${cy}" r="${h/2}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    case 'fragment':
      return `<polygon points="${cx},${cy-h} ${cx+h},${cy+h/2} ${cx-h/2},${cy+h}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    case 'eye':
      return `<ellipse cx="${cx}" cy="${cy}" rx="${h}" ry="${h*0.6}" fill="none" stroke="${stroke}" stroke-width="2"/><circle cx="${cx}" cy="${cy}" r="${h/3}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}"/>`;
    default:
      return '';
  }
}

export const PiecePalette = ({ selectedPiece, onSelect, unlockedPieces, wonShapesAvailable = {}, horizontal, testIdPrefix = '', currentConfidence = 0 }) => {
  // Combine regular pieces with won special shapes
  const allPieces = { ...PIECE_CONFIG };
  
  // Calculate dynamic confidence values for all pieces based on current zone
  const dynamicConfidences = useMemo(() => {
    const result = {};
    Object.keys(PIECE_CONFIG).forEach(type => {
      result[type] = getDynamicConfidence(type, currentConfidence);
    });
    Object.keys(SPECIAL_SHAPES_CONFIG).forEach(type => {
      result[type] = -2; // Special shapes always -2
    });
    return result;
  }, [currentConfidence]);
  
  // Add won special shapes to the palette
  Object.entries(wonShapesAvailable).forEach(([shape, count]) => {
    if (count > 0 && SPECIAL_SHAPES_CONFIG[shape]) {
      allPieces[shape] = { ...SPECIAL_SHAPES_CONFIG[shape], count, isSpecial: true };
    }
  });

  return (
    <TooltipProvider delayDuration={200}>
      <div className={horizontal ? "w-full flex justify-center" : "space-y-3"} data-testid="piece-palette">
        {!horizontal && (
          <span className="text-xs uppercase tracking-widest font-mono text-zinc-400">
            Pieces
          </span>
        )}
        <div className={horizontal ? "flex flex-wrap gap-1.5 justify-center w-full px-2" : "flex flex-col gap-2"}>
          {Object.entries(allPieces).map(([type, config]) => {
            const isSpecial = config.isSpecial;
            const remainingCount = config.count || 0;
            // Special shapes with 0 count are disabled
            const isUnlocked = isSpecial ? remainingCount > 0 : unlockedPieces.includes(type);
            const isSelected = selectedPiece === type;
            const isExhausted = isSpecial && remainingCount <= 0;

            return (
              <Tooltip key={type}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => isUnlocked && onSelect(type)}
                    disabled={!isUnlocked}
                    className={`
                      flex items-center gap-2 ${horizontal ? 'p-2' : 'p-3'} rounded-sm border transition-all duration-200 relative
                      ${isSelected
                        ? 'border-zinc-900 bg-zinc-900 text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,0.2)]'
                        : isUnlocked
                          ? isSpecial
                            ? 'border-purple-300 hover:border-purple-500 bg-purple-50 active:scale-95'
                            : 'border-zinc-200 hover:border-zinc-400 bg-white active:scale-95'
                          : isExhausted
                            ? 'border-purple-100 bg-purple-50/30 opacity-50 cursor-not-allowed'
                            : 'border-zinc-100 bg-zinc-50 opacity-40 cursor-not-allowed'
                      }
                    `}
                    data-testid={`${testIdPrefix}piece-${type}`}
                  >
                    {isSpecial && !horizontal && (
                      <Sparkles size={10} className={`absolute top-1 right-1 ${isExhausted ? 'text-purple-300' : 'text-purple-500'}`} />
                    )}
                    <svg width={horizontal ? 24 : 28} height={horizontal ? 24 : 28} viewBox="0 0 28 28"
                      dangerouslySetInnerHTML={{ __html: miniShapeSvg(type, isSelected) }}
                    />
                    {!horizontal && (
                      <div className="text-left flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{config.label}</div>
                        <div className={`text-xs font-mono ${isSelected ? 'text-zinc-300' : dynamicConfidences[type] < 0 ? 'text-green-500' : 'text-zinc-400'}`}>
                          {dynamicConfidences[type] > 0 ? '+' : ''}{dynamicConfidences[type]}
                          {isSpecial && <span className={`ml-1 ${isExhausted ? 'text-red-400' : 'text-purple-500'}`}>×{remainingCount}</span>}
                        </div>
                      </div>
                    )}
                    {!isUnlocked && !isSpecial && !horizontal && (
                      <Lock size={14} className="text-zinc-400 shrink-0" />
                    )}
                  </button>
                </TooltipTrigger>
                {!isUnlocked && !isSpecial && (
                  <TooltipContent side="right">
                    <p className="text-xs">Unlocks at {config.unlock} equilibrium{config.unlock !== 1 ? 's' : ''}</p>
                  </TooltipContent>
                )}
                {isSpecial && (
                  <TooltipContent side="right">
                    <p className="text-xs">{isExhausted ? 'All used!' : `Special shape! ${remainingCount} remaining`}</p>
                  </TooltipContent>
                )}
                {type === 'void' && !isSpecial && (
                  <TooltipContent side="right">
                    <p className="text-xs">Click on piece to erase, or empty space for void effect</p>
                    <p className="text-xs text-zinc-400 mt-1">Permanent +4 unknown penalty</p>
                  </TooltipContent>
                )}
                {type === 'pen' && (
                  <TooltipContent side="right">
                    <p className="text-xs font-medium">Draw or Rotate</p>
                    <p className="text-xs text-zinc-400 mt-1">Click shape: rotate 45°</p>
                    <p className="text-xs text-zinc-400">Drag on canvas: draw line</p>
                    <p className="text-xs text-zinc-400">Straight lines = more confidence</p>
                  </TooltipContent>
                )}
              </Tooltip>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
};
